// przycisk z listą podglądu koszyków
// @ts-nocheck

import React, { useMemo, useState, useEffect } from "react";
import classnames from "classnames";
import Popover from "@mui/material/Popover";
import Badge from "@mui/material/Badge";
import { Trans, useTranslation } from "react-i18next";
import { ArrowRight, Cart, ChevronDown } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";

import { reduxActions, useDispatch, useSelector } from "store";
import { useGetCartsAll, useGetCartPositions } from "api";
import { Button, Loader, Link, Modal } from "components/controls";
import { CartPositions } from "components/containers";

import styles from "theme/components/containers/CartsButton/CartsButton.module.scss";
import { useRWD } from "hooks";

const ButtonCart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useRWD();
  const navigate = useNavigate();

  // czy jest ustawiony element HTML popovera (użyte przy pokazywaniu modala)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement | null>(
    null
  );

  // ID domyślnego koszyka
  const { currentCartId } = useSelector((state) => state.cart);

  // pobranie danych o użytkowniku
  const { profile } = useSelector((state) => state.auth);

  // niezalogowany (profil otwarty) - nie pokazujemy mu danych handlowych koszyka
  const isOpenProfile = profile?.role === "ROLE_OPEN_PROFILE";

  // pobranie listy koszyków - dla niezalogowanego nie odpytujemy API (czekamy też na wczytanie profilu)
  const {
    data: cartsData,
    refetch: refetchCartsData,
    isFetching: isCartsFetching,
  } = useGetCartsAll({
    enabled: !!profile?.role && !isOpenProfile,
    onSuccess: (data) => {
      !currentCartId &&
        dispatch(reduxActions.setCurrentCartId(data.items[0]?.id || null));
    },
  });

  // domyślny koszyk
  const currentCart = useMemo(
    () => cartsData?.items.find((cart) => currentCartId === cart.id),
    [currentCartId, cartsData]
  );

  // pobranie stanu ładowania pozycji koszyka - NA POZIOMIE KOMPONENTU (nie w funkcji renderującej)
  const { isLoading: isCartPositionsLoading } = useGetCartPositions(
    currentCartId || 0,
    { page: 1, limit: 10 },
    {
      enabled: !!currentCartId && (currentCart?.products_count || 0) > 0,
    }
  );

  // pobieranie danych przy otwarciu popovera
  useEffect(() => {
    if (currentCart?.id) {
      refetchCartsData();
    }
  }, [currentCart?.id]);

  // ustawienie domyślnego koszyka po pobraniu/aktualizacji listy koszyków
  useEffect(() => {
    !isCartsFetching &&
      !currentCart &&
      dispatch(reduxActions.setCurrentCartId(cartsData?.items[0]?.id || null));
  }, [isCartsFetching, currentCart, cartsData]);

  useEffect(() => {
    if (currentCartId) {
      dispatch(reduxActions.setCurrentCartId(currentCartId));
    }
  }, [currentCartId]);

  const renderCartContent = () => {
    // sprawdzenie czy użytkownik jest zalogowany
    if (isOpenProfile) {
      return (
        <div
          className={styles.emptyCart}
          style={{ padding: "20px", textAlign: "center" }}
        >
          <span className={styles.title}>
            {t("Aby dodać coś do koszyka, należy najpierw się zalogować")}
          </span>
          <div style={{ marginTop: "16px" }}>
            <Button onClick={() => navigate("/login")}>
              {t("Zaloguj się")}
            </Button>
          </div>
        </div>
      );
    }

    const hasCartWithProducts =
      !!currentCartId && (currentCart?.products_count || 0) > 0;
    const isActuallyLoading =
      isCartsFetching || (hasCartWithProducts && isCartPositionsLoading);

    return (
      <>
        <div className={styles.cartWrapper}>
          <div className={styles.cartBody}>
            {isActuallyLoading ? (
              <Loader />
            ) : hasCartWithProducts ? (
              <CartPositions
                cartId={currentCartId}
                variant="small"
                onChange={() => refetchCartsData()}
              />
            ) : (
              <div className={styles.emptyCart}>
                <div className={styles.emptyCartContent}>
                  <span className={styles.title}>
                    {t("Twój koszyk jest pusty")}
                  </span>
                  <span className={styles.description}>
                    {t("Nie przegap okazji i sprawdź dostępne produkty")}!
                  </span>
                </div>
                <Link to="/products" className={styles.seeProductsButton}>
                  {t("Zobacz produkty")} <ArrowRight />
                </Link>
              </div>
            )}
          </div>

          {(currentCart?.products_count || 0) > 0 && (
            <div className={styles.cartFooter}>
              <div className={styles.total}>
                <span className={styles.label}>{t("Suma")}:</span>
                <div className={styles.prices}>
                  <span className={styles.net}>
                    <span>
                      {currentCart?.positions_value_net_formatted}{" "}
                      {currentCart?.currency}
                    </span>{" "}
                    {t("netto")}
                  </span>
                  <span className={styles.gross}>
                    <span>
                      {currentCart?.positions_value_gross_formatted}{" "}
                      {currentCart?.currency}
                    </span>{" "}
                    {t("brutto")}
                  </span>
                </div>
              </div>

              <div
                className={styles.actions}
                onClick={() => setPopoverAnchor(null)}
              >
                <Link to={`/cart/${currentCartId}`}>
                  {t("Przejdź do koszyka")} {!isMobile && <ArrowRight />}
                </Link>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderFreeDeliveryContent = () => {
    if (currentCart?.has_free_delivery) {
      return (
        <div className={styles.freeDelivery}>
          <Trans>Darmowa dostawa!</Trans>
        </div>
      );
    }

    if (currentCart?.delivery_message) {
      return (
        <div
          className={classnames(
            styles.freeDelivery,
            styles.freeDeliveryMissing
          )}
        >
          <span>{currentCart?.delivery_message}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        "StylePath-Components-Containers-CartsButton"
      )}
      onMouseLeave={() => {
        if (!isMobile) {
          setPopoverAnchor(null);
        }
      }}
    >
      <Badge
        color="secondary"
        badgeContent={0}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div
          className={classnames(styles.button, {
            [styles.active]: popoverAnchor,
          })}
          onClick={(event) => {
            setPopoverAnchor(event.currentTarget);
          }}
          onMouseEnter={(event) => {
            if (!isMobile) {
              setPopoverAnchor(event.currentTarget);
            }
          }}
        >
          {isMobile ? (
            <div>
              <Cart className={styles.icon} />
            </div>
          ) : (
            <>
              <Cart className={styles.icon} />

              {!isOpenProfile && currentCart ? (
                <div className={styles.detailsCart}>
                  <div className={styles.price}>
                    {currentCart.positions_value_net_formatted}{" "}
                    {currentCart.currency}
                  </div>
                </div>
              ) : (
                <div className={styles.detailsCart}>
                  <div className={styles.price}>0.00</div>
                </div>
              )}
              {(isOpenProfile || currentCart) && (
                <ChevronDown
                  className={classnames("arrow", { rotated: !!popoverAnchor })}
                />
              )}
            </>
          )}
        </div>
        {!isOpenProfile &&
          !isMobile &&
          !popoverAnchor &&
          currentCart?.amount_to_free_delivery_formatted &&
          renderFreeDeliveryContent()}
      </Badge>
      {isMobile ? (
        !!popoverAnchor && (
          <Modal
            title={t("Koszyk")}
            onClose={() => setPopoverAnchor(null)}
            fullScreen
          >
            {renderCartContent()}
          </Modal>
        )
      ) : (
        <Popover
          anchorEl={popoverAnchor}
          open={!!popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          PaperProps={{
            sx: { maxHeight: "calc(100% - 200px)" },
          }}
          onMouseEnter={() => {
            if (!isMobile) {
              // Utrzymaj otwarte na hover
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              setPopoverAnchor(null);
            }
          }}
        >
          {renderCartContent()}
        </Popover>
      )}
    </div>
  );
};

export default ButtonCart;
